import { handleResponseOld, makeGet, makePost } from './helpers';
import { FetchItemsCardModelsResponse } from '@/types/ItemCardModel';

/**
 * @category Item API
 * @see fetchSavedItemCounts
 */
type FetchSavedItemCountsParams = {
    deployment: string;
    itemIds: number[];
};
/**
 * Retrieves an item's saved counts via Post request to Item API
 * @function fetchSavedItemCounts
 * @category Item API
 * @param {FetchSavedItemCountsParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/saved-items/count
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/saved-search/routes/get-saved-search-items.ts
 */
const fetchSavedItemCounts = ({ deployment, itemIds }: FetchSavedItemCountsParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ deployment, path: '<ITEM-API>saved-items/count' });
        request.send({ ids: itemIds });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see fetchSavedItems
 */

export type SavedItemQueryType = {
    keyword?: string;
    page?: number;
    pageSize?: number;
    sort?: string;
    status: 'available' | 'ended';
};

type FetchSavedItemsParams = {
    authToken: string;
    bidderId: number;
    deployment: string;
    query?: SavedItemQueryType;
};

/**
 * Retrieves an item's saved counts via Post request to Item API
 * @function fetchSavedItemCounts
 * @category Item API
 * @param {FetchSavedItemCountsParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/saved-items/count
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/saved-search/routes/get-saved-search-items.ts
 */
const fetchSavedItems = ({ authToken, bidderId, deployment, query }: FetchSavedItemsParams) =>
    new Promise<FetchItemsCardModelsResponse>((resolve, reject) => {
        const request = makeGet({
            apiPath: `bidder/${bidderId}/saveditems`,
            authToken,
            deployment,
            path: '<SAVED-ITEMS-API>',
        });
        request.query(query);
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see postSaveItem
 */
type PostSaveItemParams = {
    authToken: string;
    deployment: string;
    itemId: number;
};
/**
 * Saves an item favorite via Post to Item API service
 * @function postSaveItem
 * @category Item API
 * @param {PostSaveItemParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/saved-items/save/${itemId}
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/bidder/routes/post-save-item.ts
 */
const postSaveItem = ({ authToken, deployment, itemId }: PostSaveItemParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ authToken, deployment, path: `<ITEM-API>saved-items/save/${itemId}` });
        request.send({ itemId });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

/**
 * @category Item API
 * @see postUnsaveItem
 */
type PostUnsaveItemParams = {
    authToken: string;
    deployment: string;
    itemId: number;
};
/**
 * Removes an item favorite via Post to Item API service
 * @function postUnsaveItem
 * @category Item API
 * @param {PostUnsaveItemParams}
 * @see https://item-api-DEPLOYMENT.liveauctioneers.com/saved-items/unsave/${itemId}
 * @see https://github.com/LIVEauctioneers/item-api/blob/master/src/bidder/routes/post-unsave-item.ts
 */
const postUnsaveItem = ({ authToken, deployment, itemId }: PostUnsaveItemParams) =>
    new Promise<any>((resolve, reject) => {
        const request = makePost({ authToken, deployment, path: `<ITEM-API>saved-items/unsave/${itemId}` });
        request.send({ itemId });
        request.end((err, response) => handleResponseOld({ err, reject, resolve, response }));
    });

export default {
    fetchSavedItemCounts,
    fetchSavedItems,
    postSaveItem,
    postUnsaveItem,
};
